import { ITEMS, HOTBAR, armorPoints, type Stack } from "../shared/items";
import type { Phase } from "../shared/protocol";
import { itemIcon, heartIcon, armorIcon, compassIcon } from "./textures";

const $ = (id: string) => document.getElementById(id)!;

// 10x10 white pixel icons for the Pocket Edition touch buttons.
const ICONS: Record<string, string[]> = {
  up: ["..........", "....##....", "...####...", "..######..", ".########.", "...####...", "...####...", "...####...", "...####...", ".........."],
  ul: ["..........", ".######...", ".#####....", ".######...", ".#######..", ".###.####.", ".##...###.", ".#.....#..", "..........", ".........."],
  sneak: ["..........", "...####...", "..######..", ".########.", ".########.", ".########.", ".########.", "..######..", "...####...", ".........."],
  jump: ["..........", "....##....", "...####...", "..######..", ".########.", "...####...", "...####...", "..........", ".########.", ".########."],
  chat: ["..........", ".########.", ".#......#.", ".#.####.#.", ".#......#.", ".#.###..#.", ".#......#.", ".####.###.", "....##....", "....#....."],
  pause: ["..........", "..........", "..##..##..", "..##..##..", "..##..##..", "..##..##..", "..##..##..", "..##..##..", "..........", ".........."],
  cam: ["..........", "..........", ".######...", ".#....#.#.", ".#....###.", ".#....###.", ".#....#.#.", ".######...", "..........", ".........."],
  more: ["..........", "..........", "..........", "..........", ".##.##.##.", ".##.##.##.", "..........", "..........", "..........", ".........."],
};

function iconUrl(rows: string[], rot = 0): string {
  const c = document.createElement("canvas");
  c.width = c.height = 10;
  const g = c.getContext("2d")!;
  g.translate(5, 5);
  g.rotate((rot * Math.PI) / 2);
  g.translate(-5, -5);
  g.fillStyle = "rgba(255,255,255,0.92)";
  rows.forEach((row, y) => [...row].forEach((ch, x) => ch === "#" && g.fillRect(x, y, 1, 1)));
  return c.toDataURL();
}

export class Hud {
  private root = $("hud");
  private popupEl = $("popup");
  private feedEl = $("feed");
  private titleEl = $("title");
  private subEl = $("subtitle");
  private toastEl = $("toast");
  private heartsEl = $("hearts");
  private armorEl = $("armorbar");
  private hotbarEl = $("hotbar");
  private hurtEl = $("hurtflash");
  private deathEl = $("death");
  private invEl = $("inventory");
  private chatLogEl = $("chatlog");
  private history: { msg: string; color: string }[] = [];
  private toastTimer = 0;
  private titleTimer = 0;
  private lastStats = "";
  private lastPopup = "";
  private alive = 0;
  private target = "";
  private hotbar: Stack[] = [];
  private armor: (string | null)[] = [];
  onSlot: (i: number) => void = () => {};
  touch = false;

  constructor() {
    const set = (cls: string, url: string) => document.querySelectorAll<HTMLElement>(`.ico-${cls}`).forEach((e) => (e.style.backgroundImage = `url(${url})`));
    set("up", iconUrl(ICONS.up!));
    set("right", iconUrl(ICONS.up!, 1));
    set("down", iconUrl(ICONS.up!, 2));
    set("left", iconUrl(ICONS.up!, 3));
    set("ul", iconUrl(ICONS.ul!));
    set("ur", iconUrl(ICONS.ul!, 1));
    set("sneak", iconUrl(ICONS.sneak!));
    set("jump", iconUrl(ICONS.jump!));
    set("chat", iconUrl(ICONS.chat!));
    set("pause", iconUrl(ICONS.pause!));
    set("cam", iconUrl(ICONS.cam!));

    for (let i = 0; i < HOTBAR; i++) {
      const s = document.createElement("div");
      s.className = "slot";
      s.addEventListener("pointerdown", (e) => {
        e.stopPropagation();
        this.onSlot(i);
      });
      this.hotbarEl.appendChild(s);
    }
    // The "..." button at the end of the Pocket Edition hotbar opens the inventory.
    const more = document.createElement("div");
    more.className = "slot more";
    const i = document.createElement("i");
    i.className = "ico";
    i.style.backgroundImage = `url(${iconUrl(ICONS.more!)})`;
    more.appendChild(i);
    more.addEventListener("pointerdown", (e) => {
      e.stopPropagation();
      this.openInventory();
    });
    this.hotbarEl.appendChild(more);
    $("inv-close").addEventListener("click", () => (this.invEl.hidden = true));
    $("cl-close").addEventListener("click", () => this.closeChatLog());
  }

  show(on: boolean) {
    this.root.hidden = !on;
    if (!on) {
      this.invEl.hidden = true;
      this.chatLogEl.hidden = true;
    }
  }

  get inventoryOpen() {
    return !this.invEl.hidden;
  }

  /** PocketMine-style popup above the hotbar: game state, or what you're looking at. */
  setPhase(phase: Phase, secs: number, spectating: boolean) {
    const mmss = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;
    let text = "";
    switch (phase) {
      case "waiting": text = `Waiting for tributes <b>${mmss}</b>`; break;
      case "countdown": text = `Starting in <b>${secs}</b>`; break;
      case "playing": text = `Tributes: <b>${this.alive}</b>  Time: <b>${mmss}</b>`; break;
      case "deathmatch": text = `DEATHMATCH  Tributes: <b>${this.alive}</b>  <b>${mmss}</b>`; break;
      case "ended": text = `Next round in <b>${secs}</b>`; break;
    }
    if (spectating) text = `Spectating · ${text}`;
    if (this.target) text = this.target;
    if (text !== this.lastPopup) {
      this.popupEl.innerHTML = text;
      if (phase === "countdown" && secs <= 5 && secs > 0 && !this.lastPopup.includes(`<b>${secs}</b>`)) this.title(String(secs), "", 900);
      this.lastPopup = text;
    }
    const showStats = (phase === "playing" || phase === "deathmatch") && !spectating;
    $("stats").style.visibility = showStats ? "visible" : "hidden";
  }

  setTarget(t: string) {
    this.target = t;
  }

  setAlive(n: number) {
    this.alive = n;
  }

  setStats(hp: number, armor: (string | null)[]) {
    this.armor = armor;
    const key = `${hp}|${armor.join()}`;
    if (key === this.lastStats) return;
    this.lastStats = key;
    this.heartsEl.innerHTML = "";
    for (let i = 0; i < 10; i++) {
      const v = hp - i * 2;
      const img = document.createElement("img");
      img.src = heartIcon(v >= 2 ? "full" : v >= 1 ? "half" : "empty");
      this.heartsEl.appendChild(img);
    }
    this.armorEl.innerHTML = "";
    const pts = armorPoints(armor);
    if (pts > 0) for (let i = 0; i < 10; i++) {
      const v = pts - i * 2;
      const img = document.createElement("img");
      img.src = armorIcon(v >= 2 ? "full" : v === 1 ? "half" : "empty");
      this.armorEl.appendChild(img);
    }
    if (this.inventoryOpen) this.renderInventory();
  }

  setHotbar(hotbar: Stack[], sel: number) {
    this.hotbar = hotbar;
    [...this.hotbarEl.children].slice(0, HOTBAR).forEach((el, i) => {
      const s = hotbar[i];
      el.classList.toggle("sel", i === sel);
      const want = s ? `${s.id}:${s.n}` : "";
      if ((el as HTMLElement).dataset.k === want) return;
      (el as HTMLElement).dataset.k = want;
      el.innerHTML = "";
      if (s) fillSlot(el as HTMLElement, s);
    });
    if (this.inventoryOpen) this.renderInventory();
  }

  /** Swings every compass icon on screen to `angle` (radians clockwise from up). */
  setCompass(angle: number) {
    const url = compassIcon(angle);
    if (url === compassUrl) return;
    compassUrl = url;
    document.querySelectorAll<HTMLImageElement>("img.compass").forEach((img) => (img.src = url));
  }

  /** Item name flashes above the hotbar when you switch to it. */
  showItemName(id: string | undefined) {
    if (id) this.toast(ITEMS[id]?.name ?? id);
  }

  openInventory() {
    this.invEl.hidden = false;
    if (document.pointerLockElement) document.exitPointerLock();
    this.renderInventory();
  }

  private renderInventory() {
    const armorEl = $("inv-armor"), itemsEl = $("inv-items");
    armorEl.innerHTML = "";
    itemsEl.innerHTML = "";
    for (let i = 0; i < 4; i++) {
      const s = document.createElement("div");
      s.className = "gslot";
      const id = this.armor[i];
      if (id) fillSlot(s, { id, n: 1 });
      armorEl.appendChild(s);
    }
    for (let i = 0; i < HOTBAR; i++) {
      const s = document.createElement("div");
      s.className = "gslot";
      const st = this.hotbar[i];
      if (st) fillSlot(s, st);
      itemsEl.appendChild(s);
    }
    const names = this.armor.filter(Boolean).map((id) => ITEMS[id!]!.name);
    $("inv-stats").textContent = `Armor ${armorPoints(this.armor)}/20${names.length ? " · " + names.join(", ") : ""}`;
  }

  /** A player's sign, logged like a chat line. */
  chat(from: string, text: string) {
    this.feed(`<${from}> ${text}`);
  }

  feed(msg: string, color = "#fff") {
    this.history.push({ msg, color });
    if (this.history.length > 100) this.history.shift();
    if (this.chatLogOpen) this.renderChatLog();
    const d = document.createElement("div");
    d.textContent = msg;
    d.style.color = color;
    this.feedEl.appendChild(d);
    while (this.feedEl.children.length > 5) this.feedEl.firstChild!.remove();
    setTimeout(() => (d.style.opacity = "0"), 7000);
    setTimeout(() => d.remove(), 8200);
  }

  clearFeed() {
    this.feedEl.innerHTML = "";
    this.history = [];
  }

  get chatLogOpen() {
    return !this.chatLogEl.hidden;
  }

  /** The chat screen: scrollback only. There's no box to type in. */
  openChatLog() {
    this.chatLogEl.hidden = false;
    this.feedEl.style.visibility = "hidden";
    if (document.pointerLockElement) document.exitPointerLock();
    this.renderChatLog();
  }

  closeChatLog() {
    this.chatLogEl.hidden = true;
    this.feedEl.style.visibility = "";
  }

  private renderChatLog() {
    const box = $("cl-lines");
    box.innerHTML = "";
    if (!this.history.length) {
      const d = document.createElement("div");
      d.className = "empty";
      d.textContent = "Nothing here yet";
      box.appendChild(d);
    }
    for (const { msg, color } of this.history) {
      const d = document.createElement("div");
      d.textContent = msg;
      d.style.color = color;
      box.appendChild(d);
    }
    box.scrollTop = box.scrollHeight;
  }

  title(main: string, sub: string, ms: number) {
    this.titleEl.textContent = main;
    this.subEl.textContent = sub;
    clearTimeout(this.titleTimer);
    this.titleTimer = window.setTimeout(() => {
      this.titleEl.textContent = "";
      this.subEl.textContent = "";
    }, ms);
  }

  toast(msg: string) {
    this.toastEl.textContent = msg;
    this.toastEl.classList.add("on");
    clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => this.toastEl.classList.remove("on"), 2200);
  }

  hurt() {
    this.hurtEl.classList.add("on");
    requestAnimationFrame(() => requestAnimationFrame(() => this.hurtEl.classList.remove("on")));
  }

  died() {
    this.deathEl.hidden = true;
    void this.deathEl.offsetWidth; // restart the fade animation
    this.deathEl.hidden = false;
    setTimeout(() => (this.deathEl.hidden = true), 3300);
  }
}

let compassUrl = "";

function fillSlot(el: HTMLElement, s: NonNullable<Stack>) {
  const img = document.createElement("img");
  img.src = itemIcon(s.id);
  if (s.id === "compass") {
    // The needle is live: setCompass() keeps every one of these pointing the right way.
    img.className = "compass";
    if (compassUrl) img.src = compassUrl;
  }
  img.alt = ITEMS[s.id]?.name ?? s.id;
  el.appendChild(img);
  if (s.n > 1) {
    const n = document.createElement("span");
    n.className = "n";
    n.textContent = String(s.n);
    el.appendChild(n);
  }
}
